
import { useEffect, useState } from 'react';
import romanISOMapping from '../utils/romanISOMapping.json';

/**
 * useIndicTransliteration
 * - Accepts an English (Latin) input string.
 * - Uses romanISOMapping.json to convert Google/ASCII transliteration to IAST.
 * - Dynamically imports `@indic-transliteration/sanscript` (or `sanscript`) and
 *   transliterates those Roman-Indic variants into `devanagari` and `telugu`.
 *
 * Returns { romanVariants, devanagari, telugu, loading, error }
 */

// Build lookup tables from the mapping file (case-insensitive)
const buildLookupTables = () => {
  const lookup = {};
  
  // Helper to add to lookup with lowercase key
  const addToLookup = (key, value) => {
    const lowerKey = key.toLowerCase();
    if (!lookup[lowerKey]) lookup[lowerKey] = [];
    if (!lookup[lowerKey].includes(value)) lookup[lowerKey].push(value);
  };
  
  // Add all mappings from the JSON file
  Object.entries(romanISOMapping.vowels || {}).forEach(([key, value]) => {
    addToLookup(key, value);
    // Don't add extra variants for standalone e/o - keep them as-is
  });
  
  Object.entries(romanISOMapping.consonants || {}).forEach(([key, value]) => {
    addToLookup(key, value);
  });
  
  // Add consonant+vowel combinations
  ['consonant_with_a', 'consonant_with_aa', 'consonant_with_i', 'consonant_with_ii', 
   'consonant_with_u', 'consonant_with_uu', 'consonant_with_e', 'consonant_with_o', 
   'consonant_with_ai', 'consonant_with_au'].forEach(section => {
    Object.entries(romanISOMapping[section] || {}).forEach(([key, value]) => {
      addToLookup(key, value);
      
      // For Telugu: When user types "re", they want short e (రె) by default
      // So we DON'T add the long variant automatically
      // Instead, user can type "ree" or "rE" for long ē (రే)
    });
  });
  
  // Add halant combinations
  Object.entries(romanISOMapping.halant_combinations || {}).forEach(([key, value]) => {
    addToLookup(key, value);
  });
  
  // Add common conjuncts
  Object.entries(romanISOMapping.common_conjuncts || {}).forEach(([key, value]) => {
    addToLookup(key, value);
  });
  
  // Add anusvara/visarga
  Object.entries(romanISOMapping.anusvara_visarga || {}).forEach(([key, value]) => {
    addToLookup(key, value);
  });
  
  // For each entry, also add the lowercase original key as a variant
  Object.keys(lookup).forEach(key => {
    if (!lookup[key].includes(key)) {
      lookup[key].push(key);
    }
  });
  
  return lookup;
};

const GOOGLE_TO_IAST = buildLookupTables();

// Sort keys by length (longest first) for greedy matching
const SORTED_KEYS = Object.keys(GOOGLE_TO_IAST).sort((a, b) => b.length - a.length);

export default function useIndicTransliterationCore(input, opts = {}) {
  const { cap = 200 } = opts;
  const [SanscriptLib, setSanscriptLib] = useState(null);
  const [loading, setLoading] = useState(true);
  const [romanVariants, setRomanVariants] = useState([]);
  const [devanagari, setDevanagari] = useState([]);
  const [telugu, setTelugu] = useState([]);
  const [error, setError] = useState(null);

  // Load sanscript dynamically so the hook doesn't hard-fail if the package
  // isn't present at build-time.
  useEffect(() => {
    let mounted = true;
    setLoading(true);
    (async () => {
      try {
        let mod = null;
        try {
          // Preferred package name used in many repos
          mod = await import('@indic-transliteration/sanscript');
        } catch (e1) {
          try {
            // Fallback package name
            mod = await import('sanscript');
          } catch (e2) {
            mod = null;
          }
        }
        const S = mod && (mod.default || mod);
        if (mounted) setSanscriptLib(S || null);
      } catch (e) {
        if (mounted) setError(String(e));
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    let mounted = true;
    setError(null);
    if (!input || !input.trim()) {
      setRomanVariants([]);
      setDevanagari([]);
      setTelugu([]);
      setLoading(false);
      return () => { mounted = false; };
    }

    setLoading(true);

    // Character-by-character Roman-ISO variant generator using romanISOMapping.json
    // Parse input by matching longest patterns first, then generate all variant combinations
    // Process each word separately (split by spaces)
    const generateRomanVariants = (text, limit) => {
      if (!text) return [];
      const src = text.trim();
      
      // Split by spaces to process each word separately
      const words = src.split(/\s+/);
      
      // Process each word independently
      const wordVariantsList = words.map(word => {
        if (!word) return [['']];
        
        const lower = word.toLowerCase();
        const positions = [];
        let i = 0;
        
        while (i < lower.length) {
          let matched = false;
          
          // Try to match longest pattern first (greedy matching)
          for (const key of SORTED_KEYS) {
            if (lower.slice(i, i + key.length) === key) {
              const variants = GOOGLE_TO_IAST[key];
              positions.push(variants);
              i += key.length;
              matched = true;
              break;
            }
          }
          
          // If no pattern matched, keep the character as-is
          if (!matched) {
            positions.push([lower[i]]);
            i += 1;
          }
        }
        
        return positions;
      });

      // Generate Cartesian product of all position variants for each word
      // Use iterative approach to avoid stack overflow and handle limits efficiently
      const cartesianProduct = (arrays, limit) => {
        if (arrays.length === 0) return [[]];
        
        const results = [];
        const indices = new Array(arrays.length).fill(0);
        
        while (true) {
          // Build current combination
          const combination = indices.map((idx, i) => arrays[i][idx]);
          results.push(combination);
          
          // Check limit
          if (results.length >= limit) break;
          
          // Increment indices (like counting in mixed-radix number system)
          let position = arrays.length - 1;
          while (position >= 0) {
            indices[position]++;
            if (indices[position] < arrays[position].length) {
              break; // Successfully incremented
            }
            // Overflow at this position, reset and carry to next position
            indices[position] = 0;
            position--;
          }
          
          // If we've decremented past position 0, we're done
          if (position < 0) break;
        }
        
        return results;
      };

      // Generate variants for each word separately
      const perWordLimit = Math.max(10, Math.floor(limit / words.length));
      const wordVariants = wordVariantsList.map(positions => {
        const combinations = cartesianProduct(positions, perWordLimit);
        return combinations.map(combo => combo.join(''));
      });
      
      // Combine word variants into full phrase variants
      // For multi-word input, we need to combine variants from each word
      if (wordVariants.length === 0) return [];
      if (wordVariants.length === 1) {
        const uniqueResults = Array.from(new Set(wordVariants[0]));
        if (!uniqueResults.includes(src) && uniqueResults.length < limit) {
          uniqueResults.unshift(src);
        }
        return uniqueResults.slice(0, limit);
      }
      
      // Multi-word case: combine variants from each word with spaces
      const phraseResults = [];
      const wordIndices = new Array(wordVariants.length).fill(0);
      
      while (phraseResults.length < limit) {
        // Build current phrase by joining word variants
        const phrase = wordIndices.map((idx, wordIdx) => wordVariants[wordIdx][idx]).join(' ');
        phraseResults.push(phrase);
        
        // Increment word variant indices
        let pos = wordVariants.length - 1;
        while (pos >= 0) {
          wordIndices[pos]++;
          if (wordIndices[pos] < wordVariants[pos].length) {
            break;
          }
          wordIndices[pos] = 0;
          pos--;
        }
        
        // If we've exhausted all combinations, stop
        if (pos < 0) break;
      }
      
      // Ensure original is included
      const uniqueResults = Array.from(new Set(phraseResults));
      if (!uniqueResults.includes(src) && uniqueResults.length < limit) {
        uniqueResults.unshift(src);
      }
      
      return uniqueResults.slice(0, limit);
    };

    const romans = generateRomanVariants(input, cap);
    if (!mounted) return () => { mounted = false; };
    setRomanVariants(romans);

    const doTransliteration = () => {
      if (!SanscriptLib) {
        // Sanscript not available yet; we'll leave script arrays empty for now.
        setDevanagari([]);
        setTelugu([]);
        setLoading(false);
        return;
      }

      // Helper: detect whether a candidate string contains any *independent*
      // vowel letter (not vowel signs) at an index > 0 **within the same word**.
      // For multi-word input, we check each word separately (split by space).
      // Only independent vowels mid-word are undesirable.
      const hasVowelNotAtStart = (s, script) => {
        if (!s) return false;
        
        // Split by space to check each word separately
        const words = s.split(' ');
        
        for (const word of words) {
          if (!word) continue;
          
          if (script === 'devanagari') {
            // independent vowels U+0904–U+0914
            const re = /[\u0904-\u0914]/g;
            let m;
            while ((m = re.exec(word)) !== null) {
              if (m.index > 0) return true; // vowel mid-word
            }
          } else if (script === 'telugu') {
            // independent vowels U+0C05–U+0C14
            const re = /[\u0C05-\u0C14]/g;
            let m;
            while ((m = re.exec(word)) !== null) {
              if (m.index > 0) return true; // vowel mid-word
            }
          }
        }
        
        return false;
      };

      try {
        const dev = [];
        const tel = [];
        for (const r of romans) {
          try {
            // Try transliterating the original candidate first. Only if the
            // produced output contains the vocalic-L (or similar undesired
            // characters) do we fall back to an ASCII-safe form.
            // Convert IAST to ITRANS format for proper Telugu e/o handling
            let itransR = r
              .replace(/ā/g, 'A').replace(/ī/g, 'I').replace(/ū/g, 'U')
              .replace(/ṛ/g, 'RRi').replace(/ṝ/g, 'RRI')
              .replace(/ḷ/g, 'LLi').replace(/ḹ/g, 'LLI')
              .replace(/ṅ/g, '~N').replace(/ñ/g, '~n')
              .replace(/ṭ/g, 'T').replace(/ḍ/g, 'D').replace(/ṇ/g, 'N')
              .replace(/ś/g, 'sh').replace(/ṣ/g, 'Sh')
              .replace(/ṃ/g, 'M').replace(/ḥ/g, 'H')
              .replace(/ē/g, 'E').replace(/ō/g, 'O')  // Long e/o
              .replace(/ṟ/g, 'R').replace(/ḻ/g, 'lh');  // Telugu-specific
            
            const dRaw = SanscriptLib.t(itransR, 'itrans', 'devanagari');
            const tRaw = SanscriptLib.t(itransR, 'itrans', 'telugu');

            // Devanagari vocalic L = U+090C (ऌ), Telugu vocalic L = U+0C8C (ఌ)
            const hasVocalicLDeva = dRaw && /\u090C/.test(dRaw);
            const hasVocalicLTel = tRaw && /\u0C8C/.test(tRaw);

            let d = dRaw;
            let t = tRaw;
            
            // Telugu short e/o fix: ITRANS treats 'e' and 'o' as long by default.
            // For Telugu, we need to also generate short e (ె) and short o (ొ) variants.
            // Long e = ే (U+0C47), Short e = ె (U+0C46)
            // Long o = ో (U+0C4B), Short o = ొ (U+0C4A)
            let tShortE = null;
            let tShortO = null;
            if (tRaw) {
              if (tRaw.includes('\u0C47')) { // has long e
                tShortE = tRaw.replace(/\u0C47/g, '\u0C46'); // replace with short e
              }
              if (tRaw.includes('\u0C4B')) { // has long o
                tShortO = tRaw.replace(/\u0C4B/g, '\u0C4A'); // replace with short o
              }
            }

            if (hasVocalicLDeva || hasVocalicLTel) {
              // compute ASCII-safe fallback and re-run transliteration
              let safe = String(r);
              safe = safe.replace(/ḷ([aeiouāīūṛ])/gi, 'l$1');
              safe = safe.replace(/ḻ([aeiouāīūṛ])/gi, 'l$1');
              safe = safe.replace(/ṟ([aeiouāīūṛ])/gi, 'r$1');
              safe = safe.replace(/ṉ([aeiouāīūṛ])/gi, 'n$1');
              safe = safe.replace(/ḍ([aeiouāīūṛ])/gi, 'd$1');
              safe = safe.replace(/ṭ([aeiouāīūṛ])/gi, 't$1');

              try {
                d = SanscriptLib.t(safe, 'itrans', 'devanagari');
              } catch (e) { d = dRaw; }
              try {
                t = SanscriptLib.t(safe, 'itrans', 'telugu');
              } catch (e) { t = tRaw; }
            }
            // helper to detect script blocks
            const hasDevanagari = (s) => /[\u0900-\u097F]/.test(s);
            const hasTelugu = (s) => /[\u0C00-\u0C7F]/.test(s);

            // Prefer correct-script outputs: ensure devanagari suggestions really contain Devanagari characters.
            if (d && hasDevanagari(d) && !hasVowelNotAtStart(d, 'devanagari')) dev.push(d);
            // If `d` doesn't look like Devanagari but `t` is Devanagari (rare), accept it as devanagari
            else if ((!d || !hasDevanagari(d)) && t && hasDevanagari(t) && !hasVowelNotAtStart(t, 'devanagari')) dev.push(t);

            // Add Telugu suggestions (original long e/o version)
            if (t && hasTelugu(t) && !hasVowelNotAtStart(t, 'telugu')) tel.push(t);
            else if ((!t || !hasTelugu(t)) && d && hasTelugu(d) && !hasVowelNotAtStart(d, 'telugu')) tel.push(d);
            
            // Add Telugu short e/o variants
            if (tShortE && hasTelugu(tShortE) && !hasVowelNotAtStart(tShortE, 'telugu')) tel.push(tShortE);
            if (tShortO && hasTelugu(tShortO) && !hasVowelNotAtStart(tShortO, 'telugu')) tel.push(tShortO);

            // If the roman candidate contains dotted lateral 'ḷ' (or ḻ)
            // and Sanscript did not produce the Telugu retroflex-l (ళ U+0C33),
            // build a manual Telugu candidate by transliterating the left/right
            // context and inserting the codepoint for ళ plus a vowel sign.
            if ((/ḷ|ḻ/.test(r))) {
              const TEL_RETROFLEX_L = '\u0C33';
              const alreadyHasRetro = (t && t.indexOf('\u0C33') !== -1);
              if (!alreadyHasRetro) {
                try {
                  const idx = r.indexOf('ḷ') >= 0 ? r.indexOf('ḷ') : r.indexOf('ḻ');
                  const left = r.slice(0, idx);
                  const rest = r.slice(idx + 1);

                  // detect vowel immediately following ḷ/ḻ
                  const vowelPatterns = ['aa','ā','ii','ī','uu','ū','ee','ē','e','i','u','o','ō'];
                  let vowel = '';
                  for (const p of vowelPatterns) {
                    if (rest.startsWith(p)) { vowel = p; break; }
                  }
                  const afterVowel = rest.slice(vowel.length);

                  const vowelToTel = {
                    'aa': '\u0C3E','ā':'\u0C3E',
                    'i':'\u0C3F','ī':'\u0C40','ii':'\u0C40',
                    'u':'\u0C41','ū':'\u0C42','uu':'\u0C42',
                    'e':'\u0C46','ee':'\u0C47','ē':'\u0C47',
                    'o':'\u0C4A','ō':'\u0C4C'
                  };

                  const vowelSign = vowelToTel[vowel] || '';

                  const leftTel = left ? SanscriptLib.t(left, 'itrans', 'telugu') : '';
                  const rightTel = afterVowel ? SanscriptLib.t(afterVowel, 'itrans', 'telugu') : '';

                  const manualTel = (leftTel || '') + TEL_RETROFLEX_L + (vowelSign || '') + (rightTel || '');
                  // only add if it's not empty and looks like Telugu
                  if (manualTel && /[\u0C00-\u0C7F]/.test(manualTel) && !hasVowelNotAtStart(manualTel, 'telugu')) tel.push(manualTel);
                } catch (e) {
                  // ignore manual construction failures
                }
              }
            }

            // Do the same for Devanagari (Sanskrit/Hindi) when dotted lateral
            // present but Sanscript didn't produce Devanagari retroflex-l (ळ U+0933).
            if ((/ḷ|ḻ/.test(r))) {
              const DEV_RETROFLEX_L = '\u0933';
              const alreadyHasDevaRetro = (d && d.indexOf('\u0933') !== -1);
              if (!alreadyHasDevaRetro) {
                try {
                  const idx2 = r.indexOf('ḷ') >= 0 ? r.indexOf('ḷ') : r.indexOf('ḻ');
                  const left2 = r.slice(0, idx2);
                  const rest2 = r.slice(idx2 + 1);

                  const vowelPatterns2 = ['aa','ā','ii','ī','uu','ū','ee','ē','e','i','u','o','ō'];
                  let vowel2 = '';
                  for (const p of vowelPatterns2) {
                    if (rest2.startsWith(p)) { vowel2 = p; break; }
                  }
                  const afterVowel2 = rest2.slice(vowel2.length);

                  const vowelToDeva = {
                    'aa':'\u093E','ā':'\u093E',
                    'i':'\u093F','ī':'\u0940','ii':'\u0940',
                    'u':'\u0941','ū':'\u0942','uu':'\u0942',
                    'e':'\u0947','ee':'\u0947','ē':'\u0947',
                    'o':'\u094B','ō':'\u094C'
                  };

                  const vowelSign2 = vowelToDeva[vowel2] || '';

                  const leftDeva = left2 ? SanscriptLib.t(left2, 'itrans', 'devanagari') : '';
                  const rightDeva = afterVowel2 ? SanscriptLib.t(afterVowel2, 'itrans', 'devanagari') : '';

                  const manualDeva = (leftDeva || '') + DEV_RETROFLEX_L + (vowelSign2 || '') + (rightDeva || '');
                  if (manualDeva && /[\u0900-\u097F]/.test(manualDeva) && !hasVowelNotAtStart(manualDeva, 'devanagari')) dev.push(manualDeva);
                } catch (e) {
                  // ignore manual failures
                }
              }
            }
          } catch (e) {
            // skip mapping failures for individual candidates
          }
          if (dev.length >= cap && tel.length >= cap) break;
        }
        // Fallback: if we couldn't produce any Devanagari outputs, try a direct transliteration of roman candidates again
        let finalDev = Array.from(new Set(dev)).slice(0, cap);
        if (finalDev.length === 0) {
          for (const r of romans) {
              try {
                const alt = SanscriptLib.t(r, 'itrans', 'devanagari');
                if (alt && /[\u0900-\u097F]/.test(alt) && !hasVowelNotAtStart(alt, 'devanagari')) finalDev.push(alt);
              } catch (e) {}
            if (finalDev.length >= cap) break;
          }
          finalDev = Array.from(new Set(finalDev)).slice(0, cap);
        }

        if (mounted) {
          setDevanagari(finalDev);
          
          // Sort Telugu results: prioritize words without ఱ (RRA U+0C31), push ఱ words to end
          // Also filter out any suggestions that contain English/ASCII characters or special symbols
          const uniqueTel = Array.from(new Set(tel));
          const filteredTel = uniqueTel.filter(t => {
            // Check if string contains only valid Telugu characters and spaces
            // Telugu range: U+0C00-U+0C7F
            // Reject: English letters, special ASCII symbols, IAST diacritics
            return !/[a-zA-Z~`!@#$%^&*()_+=\[\]{};':"\\|,.<>?/\u0300-\u036F\u1DC0-\u1DFF]/.test(t);
          });
          const sortedTel = filteredTel.sort((a, b) => {
            const aHasRRA = a.includes('\u0C31'); // ఱ
            const bHasRRA = b.includes('\u0C31');
            
            // If only one has ఱ, push it to the end
            if (aHasRRA && !bHasRRA) return 1;
            if (!aHasRRA && bHasRRA) return -1;
            
            // Otherwise maintain original order
            return 0;
          }).slice(0, cap);
          
          setTelugu(sortedTel);
          setLoading(false);
        }
      } catch (e) {
        if (mounted) { setError(String(e)); setLoading(false); }
      }
    };

    // If Sanscript already loaded, run immediately; otherwise wait briefly for
    // the loader effect to finish (it runs on mount).
    if (SanscriptLib) {
      doTransliteration();
    } else {
      const id = setTimeout(() => { doTransliteration(); }, 250);
      return () => { 
        clearTimeout(id);
        mounted = false;
      };
    }

    return () => { mounted = false; };
  }, [input, SanscriptLib, cap]);

  return { romanVariants, devanagari, telugu, loading, error };
}

// Small utility: quick sync transliterate if a global Sanscript is present
export function quickTransliterateSync(romanText, target = 'devanagari') {
  try {
    const S = (typeof window !== 'undefined' && window.Sanscript) ? window.Sanscript : null;
    if (!S) return null;
    return S.t(romanText, 'iast', target);
  } catch (e) {
    return null;
  }
}

// Named wrapper used by UI components. The project previously imported a
// named `useIndicTransliteration`; provide that API by delegating to the core hook
// and returning `suggestions` shaped output the UI expects.
export function useIndicTransliteration(input, opts = {}) {
  const { romanVariants, devanagari, telugu, loading, error } = useIndicTransliterationCore(input, opts);
  return {
    suggestions: {
      english: romanVariants || [],
      telugu: telugu || [],
      sanskrit: devanagari || []
    },
    loading,
    error
  };
}

